import get from 'lodash/get';
import range from 'lodash/range';
import isEqual from 'lodash/isEqual';

import { RESPONSIVE_CAROUSEL, RESPONSIVE_CAROUSEL_APP } from 'lib/contract/names';
import { getLinkFromHTML, isBlank, removeHTML } from 'lib/string';
import personalizedHomepage from 'lib/templates/personalizedHomepage';
import { getAllComponents } from 'utils/factory';
import { CONTENT_POOL_EXCEPTION_MESSAGE, LINK_PREFIX } from './const';
import { validateComponentName, validatePageName } from './nameValidations';

/**
 * validates component context selection
 * @param {object} component - component Object
 * @returns {Array} returns array of validation failures.
 */
const isContentContextSelected = function isContentContextSelected(component) {
  let message = '';
  const { MetadataFields } = get(component, 'Payload', {});
  if (MetadataFields && MetadataFields.ContentContext) {
    const devices = MetadataFields.ContentContext.Device;
    const country = MetadataFields.ContentContext.CountryCode;
    if (!devices.length > 0) {
      message = 'At least one Device is required in Display Options. ';
    }
    if (!country.length > 0) {
      message += 'At least one Country is required in Display Options.';
    }
  }
  return message ? { ContentContext: { message } } : undefined;
};

/**
 * validate whether a dynamic component route field is populated
 * @param {object} component - component
 * @returns {Array} returns array of validation failures
 */
export const isDynamicComponentWithRoute = function isDynamicComponentWithRoute(component) {
  if (!component.IsDynamic || component.IsCompassComponent) {
    return undefined;
  }

  let message = '';
  if (!component.ComponentRoute) {
    message = 'Component route is required.';
  } else if (component.ComponentRoute.length < 3) {
    message = 'Component route must be at least 3 characters.';
  }

  return message ? { DynamicComponent: { message } } : undefined;
};

/**
 * validate that an error isn't thrown for values given to the URL() constructor
 * @param {object} param - param
 * @param {string} param.value - URL value to validate
 * @param {boolean} param.isRelative - type of URL is 'relative' or not
 * @returns {undefined|string} - undefined for passed validation, error message string for failed validation
 */
const validateUsingUrlConstructor = function validateUsingUrlConstructor({
  value,
  isRelative
}) {
  try {
    const url = new URL(isRelative ? `https://www.nordstrom.com${value}` : value);
    const supportedProtocol = [
      LINK_PREFIX.HTTP,
      LINK_PREFIX.HTTPS,
      LINK_PREFIX.MAILTO,
      LINK_PREFIX.TEL,
      LINK_PREFIX.NORDSTROM
    ];
    if (supportedProtocol.includes(url.protocol)) {
      return undefined;
    }
  } catch (error) {
    // errror handling for new URL() - if error is thrown, return failed validation message
    return "This link looks broken. Check to ensure it's correct.";
  }
  return undefined;
};

/**
 * perform the url validation for a given absolute url field value
 * @param {object} param - param
 * @param {string} param.value - absolute url field value to validate
 * @returns {string} returns failure message string for failed validation
 */
const processAbsoluteURLValidation = function processAbsoluteURLValidation({ value }) {
  // after http|https:// - absolute link values must include a domain (examples: www or nordstrom), followed by a top-level domain (examples: .com or .org)
  const absoluteRegex = /[a-zA-Z0-9@:%._\\+~#?&//=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%._\\+~#?&//=-]*)/;
  if (!absoluteRegex.test(value)) {
    return "This link looks broken. Check to ensure it's correct.";
  }

  // absolute link values cannot include the following values twice: `http:`, `https:`, nor `www.`
  if (value.indexOf(LINK_PREFIX.HTTP) !== value.lastIndexOf(LINK_PREFIX.HTTP)) {
    return "This link cannot include 'http:' more than once.";
  }
  if (value.indexOf(LINK_PREFIX.HTTPS) !== value.lastIndexOf(LINK_PREFIX.HTTPS)) {
    return "This link cannot include 'https:' more than once.";
  }
  if (value.indexOf(LINK_PREFIX.WWW) !== value.lastIndexOf(LINK_PREFIX.WWW)) {
    return "This link cannot include 'www.' more than once.";
  }

  // absolute link values can't include both `https:` and `http:` in the same link
  if (value.includes(LINK_PREFIX.HTTP) && value.includes(LINK_PREFIX.HTTPS)) {
    return "This link cannot include both 'https:' and 'http:'.";
  }
  return validateUsingUrlConstructor({ value });
};

/**
 * perform the url validation for a given relative url field value
 * @param {object} param - param
 * @param {string} param.value - relative url field value to validate
 * @returns {string} returns failure message string for failed validation
 */
const processRelativeURLValidation = function processRelativeURLValidation({ value }) {
  // relative link values can't include the following values: `http:`, `https:`, nor `www.`
  if (value.includes(LINK_PREFIX.HTTP) || value.includes(LINK_PREFIX.HTTPS)) {
    return `Links that start with ‘${LINK_PREFIX.RELATIVE}‘ cannot include ‘http:’ nor ‘https:’`;
  } else if (value.includes(LINK_PREFIX.WWW)) {
    return `Links that start with ‘${LINK_PREFIX.RELATIVE}‘ cannot include ‘www.’`;
  }
  return validateUsingUrlConstructor({ value, isRelative: true });
};

/**
 * perform the url validation for a given hlios:// or hlios-rack:// link field value
 * @param {object} param - param
 * @param {string} param.value - hlios:// or hlios-rack:// link field value to validate
 * @returns {string} returns failure message string for failed validation
 */
const processHliosLinkValidation = function processHliosLinkValidation({ value }) {
  const linkType = value.startsWith(LINK_PREFIX.HLIOS_RACK)
    ? LINK_PREFIX.HLIOS_RACK
    : LINK_PREFIX.HLIOS;
  const link = value.slice(linkType.length);

  // hlios:// or hlios-rack:// links must include a route
  if (!link) {
    return `Please include a route after '${linkType}''`;
  }

  // hlios:// or hlios-rack:// links cannot include 'http:' or 'https:' or 'www.'
  if (link.includes(LINK_PREFIX.HTTP)) {
    return `Links that start with ${linkType} cannot include http:`;
  } else if (link.includes(LINK_PREFIX.HTTPS)) {
    return `Links that start with ${linkType} cannot include https:`;
  } else if (link.includes(LINK_PREFIX.WWW)) {
    return `Links that start with ${linkType} cannot include www.`;
  }
  return undefined;
};

/**
 * perform the url validation for a given anchor link field value
 * @param {object} param - param
 * @param {string} param.value - anchor link field value to validate
 * @returns {string} returns failure message string for failed validation
 */
const processAnchorLinkValidation = function processAnchorLinkValidation({ value }) {
  // anchor links must include a component id / anchor link after '#'
  const id = value.slice(LINK_PREFIX.ANCHOR.length);
  if (!id) {
    return 'Please include an anchor link after `#`';
  }
  return undefined;
};

/**
 * perform the url validation for a given nordstrom:// link field value
 * @param {object} param - param
 * @param {string} param.value - nordstrom:// link field value to validate
 * @returns {string} returns failure message string for failed validation
 */
const processNordstromLinkValidation = function processNordstromLinkValidation({ value }) {
  const link = value.slice(LINK_PREFIX.NORDSTROM.length);

  // 'nordstrom://' links must include a route
  if (!link) {
    return 'Please include a route after `nordstrom://`';
  }

  // 'nordstrom://' links cannot include 'http:' or 'https:' or 'www.'
  if (link.includes(LINK_PREFIX.HTTP)) {
    return 'Links that start with ‘nordstrom://‘ cannot include ‘http:’';
  } else if (link.includes(LINK_PREFIX.HTTPS)) {
    return 'Links that start with ‘nordstrom://‘ cannot include ‘https:’';
  } else if (link.includes(LINK_PREFIX.WWW)) {
    return 'Links that start with ‘nordstrom://‘ cannot include ‘www.’';
  }
  return validateUsingUrlConstructor({ value });
};

/**
 * perform the url validation for a given mailto link field value
 * @param {object} param - param
 * @param {string} param.value - mailto link field value to validate
 * @returns {string} returns failure message string for failed validation
 */
const processMailtoLinkValidation = function processMailtoLinkValidation({ value }) {
  // mailto link values must be in the following format: example@email.com (or .org, .edu, .anything)
  const mailtoLink = value.slice(LINK_PREFIX.MAILTO.length);
  const mailtoRegex = /\S+@\S+\.\S+/;
  if (!mailtoRegex.test(mailtoLink)) {
    return 'This email looks broken. Check to ensure it’s correct.';
  }
  return validateUsingUrlConstructor({ value });
};

/**
 * perform the url validation for a given tel link field value
 * @param {object} param - param
 * @param {string} param.value - tel link field value to validate
 * @returns {string} returns failure message string for failed validation
 */
const processTelLinkValidation = function processTelLinkValidation({ value }) {
  const telLink = value.slice(LINK_PREFIX.TEL.length);
  const telRegex = /^[^A-Za-z:\-().]+$/g;
  if (!telLink.startsWith('+1')) {
    // tel link values cannot include letters or special characters
    return 'Phone number must start with "+1".';
  } else if (!telRegex.test(telLink) || telLink.length !== 12) {
    // tel link value cannot include characters outside of + and numbers 0-9
    // AND tel link value must be 12 characters long (+1 country code, plus 10 digit phone number)
    return 'This phone number looks broken. Check to ensure it’s correct.';
  }
  return validateUsingUrlConstructor({ value });
};

/**
 * perform the url validation for a given field value
 * @param {object} param - param
 * @param {string} param.value - field value to validate
 * @returns {string} returns failure message string
 */
// eslint-disable-next-line consistent-return
const processURLValidation = function processURLValidation({ value }) {
  let urlType = null;

  // value must start with one of the following values: `http://`, `https://`, `hlios://`, `hlios-rack://`, `/`, `nordstrom://`, '#', 'mailto:`, or `tel:`
  if (
    value.startsWith(LINK_PREFIX.HTTP.concat('//')) ||
    value.startsWith(LINK_PREFIX.HTTPS.concat('//'))
  ) {
    urlType = LINK_PREFIX.ABSOLUTE_NAME;
  } else if (value.startsWith(LINK_PREFIX.RELATIVE)) {
    urlType = LINK_PREFIX.RELATIVE;
  } else if (value.startsWith(LINK_PREFIX.HLIOS)) {
    urlType = LINK_PREFIX.HLIOS;
  } else if (value.startsWith(LINK_PREFIX.HLIOS_RACK)) {
    urlType = LINK_PREFIX.HLIOS_RACK;
  } else if (value.startsWith(LINK_PREFIX.ANCHOR)) {
    urlType = LINK_PREFIX.ANCHOR;
  } else if (value.startsWith(LINK_PREFIX.NORDSTROM)) {
    urlType = LINK_PREFIX.NORDSTROM;
  } else if (value.startsWith(LINK_PREFIX.MAILTO)) {
    urlType = LINK_PREFIX.MAILTO;
  } else if (value.startsWith(LINK_PREFIX.TEL)) {
    urlType = LINK_PREFIX.TEL;
  }
  if (!urlType) {
    return 'Links must start with "/", "http://", "https://", "hlios://", "hlios-rack://", "nordstrom://", "#", "mailto:", or "tel:"';
  }

  // value cannot include a space
  if (value.includes(' ')) {
    return 'This link contains a space. Remove it to fix the link.';
  }

  switch (urlType) {
    case LINK_PREFIX.ABSOLUTE_NAME:
      return processAbsoluteURLValidation({ value });
    case LINK_PREFIX.RELATIVE:
      return processRelativeURLValidation({ value });
    case LINK_PREFIX.ANCHOR:
      return processAnchorLinkValidation({ value });
    case LINK_PREFIX.NORDSTROM:
      return processNordstromLinkValidation({ value });
    case LINK_PREFIX.HLIOS:
    case LINK_PREFIX.HLIOS_RACK:
      return processHliosLinkValidation({ value });
    case LINK_PREFIX.MAILTO:
      return processMailtoLinkValidation({ value });
    case LINK_PREFIX.TEL:
      return processTelLinkValidation({ value });
    default:
      break;
  }
};

/**
 * validates field value against validation in given contract
 * @param {string} fieldValue - value of the field to be validated
 * @param {object} fieldContract - contract of the field to validate against
 * @param {string} fieldContract.canvasWidget
 * @param {number} fieldContract.maxLength
 * @param {number} fieldContract.minLength
 * @param {string} fieldContract.name
 * @param {string} fieldContract.regex
 * @param {boolean} fieldContract.required
 * @param {string} fieldContract.regexMessage
 * @param {boolean} fieldContract.validateURL
 * @returns {string} a message describing the first failed validation
 */
export const validateFieldValue = function validateFieldValue(
  fieldValue,
  {
    canvasWidget,
    maxLength,
    minLength,
    name,
    regex,
    required,
    regexMessage = 'no additional information.',
    validateURL
  } = {}
) {
  const isString = typeof fieldValue === 'string';
  let value;

  // remove href link(s) values from LiveTextEditors including '</a>'
  if (canvasWidget === 'LiveTextEditor' && fieldValue?.includes('</a>')) {
    const inlineLinks = getLinkFromHTML(fieldValue);

    // handle multiple inline links
    value = inlineLinks.length === 1 ? inlineLinks[0] : inlineLinks;
  } else if (canvasWidget === 'LiveTextEditor' && !fieldValue?.includes('</a>')) {
    // return undefined if the LiveTextEditor doesn't have an inline link
    return undefined;
  } else {
    value = isString ? removeHTML(fieldValue) : fieldValue;
  }

  // ignore validations if field is empty and optional
  if (!required && !value) {
    return undefined;
  }

  // fail on nonexistent or blank values
  if (required && (!value || (isString && isBlank(value)))) {
    return `'${name}' is required.`;
  }

  // fail on values that are too long
  if (maxLength && value && value.length > maxLength) {
    return `'${name}' has maximum length ${maxLength}.`;
  }

  // fail on nonexistent or values that are too short
  if (minLength && (!value || value.length < minLength)) {
    return `'${name}' has minimum length ${minLength}.`;
  }

  // fail on values that don't match the regex
  if (regex) {
    const r = new RegExp(regex);
    if (!r.test(fieldValue)) {
      return `'${fieldValue}' is not valid for '${name}': ${regexMessage}`;
    }
  }

  // fail on values that don't pass malformed link validation
  if (validateURL) {
    // handle multiple inline links
    if (Array.isArray(value)) {
      // filtering out 'undefined' values to avoid empty failed validation messages in UI
      const failureMessages = value
        .map(val => processURLValidation({ value: val }))
        .filter(Boolean);
      if (failureMessages.length) {
        return failureMessages;
      }
    } else {
      return processURLValidation({ value });
    }
  }

  return undefined;
};

/**
 * identifies whether or not a component is a content pool placement
 * @param {object} params - function params
 * @param {object} params.component - current component to save
 * @returns {boolean} whether or not a component is a content pool placement
 */
export const isContentPoolPlacement = ({ component }) => {
  const supportedContentPoolPlacementTypes = [RESPONSIVE_CAROUSEL, RESPONSIVE_CAROUSEL_APP];
  if (
    supportedContentPoolPlacementTypes.includes(component.Variation) &&
    component.Payload.useContentPool
  ) {
    return true;
  }
  return false;
};

/**
 * validates current data in component object against contract.
 * @param {object} component - component object from redux store.
 * @param {object} componentContract - component's contract for data validation.
 * @returns {Array} returns array containing failed validations.
 */
export const validateComponentFields = function validateComponentFields(
  component,
  componentContract
) {
  const failedValidations = {};
  const queue = [];
  if (!component || !componentContract) {
    return failedValidations;
  }
  queue.push({ replaceIndicies: [], contract: componentContract });
  while (queue.length) {
    const { replaceIndicies, contract } = queue.shift();

    const location = replaceIndicies.reduce(
      (loc, ind) => loc.replace(/indexValue[0-9]/, ind),
      contract.propLocation || ''
    );

    if (contract.children) {
      const isArray = contract.type === 'array';
      const arrayFieldLength = get(component, location, []).length;

      if (isArray) {
        range(arrayFieldLength).forEach(index => {
          queue.push(
            ...contract.children.map(childContract => ({
              replaceIndicies: [...replaceIndicies, index],
              contract: childContract
            }))
          );
        });
      } else {
        queue.push(
          ...contract.children.map(childContract => ({
            replaceIndicies: [...replaceIndicies],
            contract: childContract
          }))
        );
      }
    } else {
      const fieldValue = get(component, location);
      const failedValidation = validateFieldValue(fieldValue, contract);

      // allow content pool placements to bypass failed child component validations
      const canBypassFailedValidation = (comp = {}, validation = '') => {
        if (
          validation === CONTENT_POOL_EXCEPTION_MESSAGE &&
          isContentPoolPlacement({ component: comp })
        ) {
          return true;
        }
        return false;
      };
      if (failedValidation && !canBypassFailedValidation(component, failedValidation)) {
        failedValidations[location] = {
          message: failedValidation,
          componentName: component.ComponentName
        };
      }
    }
  }
  return failedValidations;
};

/**
 * validated the given component
 * @param {Array} components - array of components to be validated
 * @param {Array} contractMap - contract mapping
 * @returns {object} object having all of the failed validations.
 */
const areValidComponents = function areValidComponents(components, contractMap) {
  const failedValidations = {};
  components.forEach(component => {
    const variation = component.Variation || component.SchemaName;
    const fullContract = contractMap[variation] || {};

    // the component Id is undefined (this is because container components are
    // list of strings(Error) instead of list of objects)
    // pushing a hotfix. will circle back to fix this properly.
    if (component.Id) {
      failedValidations[component.Id] = {
        ...validateComponentFields(component, fullContract),
        ...isContentContextSelected(component),
        ...isDynamicComponentWithRoute(component)
      };
      const nameValidation = validateComponentName(
        component.ComponentName,
        component.IsDynamic
      );
      if (nameValidation) {
        failedValidations[component.Id].ComponentName = { message: nameValidation };
      }
    }
  });
  return failedValidations;
};

/**
 * validated the given component
 * @param {object} component - to be validated
 * @param {object} contractMap - contract mapping
 * @returns {object} object having all of the failed validations.
 */
export const isValidComponent = function isValidComponent(component, contractMap) {
  const components = getAllComponents(component, true);
  return areValidComponents(components, contractMap);
};

/**
 * validates page object before making a save request.
 *
 * @param {object} pagePayload - pagePayload to be saved
 * @param {object} template - template for page
 * @returns {Array} returns array of validation failures.
 */
export const isPageTemplateValid = function(pagePayload, template) {
  const failedTemplateValidations = {};

  if (template.metaData.Route) {
    const routeValidation = isEqual(
      template.metaData.Route,
      get(pagePayload, 'Payload.MetadataFields.Route', {})
    );
    if (!routeValidation) {
      failedTemplateValidations.TemplateRoute = {
        message: 'Incorrect routes for specified template'
      };
    }
  }

  if (template.metaData.CategoryId) {
    const categoryIdValidation = isEqual(
      template.metaData.CategoryId,
      get(pagePayload, 'Payload.MetadataFields.CategoryId', 0)
    );
    if (!categoryIdValidation) {
      failedTemplateValidations.TemplateCategoryId = {
        message: 'Incorrect category ID for specified template'
      };
    }
  }

  if (template.metaData.SeoSettings) {
    const seoSettingsValidation = isEqual(
      template.metaData.SeoSettings,
      get(pagePayload, 'Payload.MetadataFields.SeoSettings', {})
    );
    if (!seoSettingsValidation) {
      failedTemplateValidations.TemplateSeoSettings = {
        message: 'Incorrect seo settings for specified template'
      };
    }
  }

  const commponents = get(pagePayload, 'Payload.Components', []);

  // validate components
  template.requiredComponents.forEach((requiredComponent, index) => {
    let presentComponents = commponents.filter(
      item => get(item, 'Variation', '') === requiredComponent.componentVariation
    );

    if (requiredComponent.templateName) {
      presentComponents = presentComponents.filter(
        item =>
          get(item, 'Payload.ComponentTemplate.TemplateName', '') ===
          requiredComponent.templateName
      );
    }
    if (presentComponents.length < requiredComponent.minRequired) {
      failedTemplateValidations[`TemplateComponents_${index}`] = {
        message: `Missing ${requiredComponent.componentVariation} components`
      };
    }
  });

  return failedTemplateValidations;
};

/**
 * validates page object before making a save request.
 *
 * @param {object} pagePayload - pagePayload to be saved
 * @param {object} contractMap - contract mapping
 * @returns {Array} returns array of validation failures.
 */
export const isPageValid = function isPageValid(pagePayload, contractMap) {
  const failedPageValidations = {};

  // Page Props Validations
  const pageNameValidation = validatePageName(pagePayload.PageName);
  if (pageNameValidation.length) {
    failedPageValidations.PageName = { message: pageNameValidation };
  }
  if (!pagePayload.IsCompassPage) {
    const routes = get(pagePayload, 'Payload.MetadataFields.Route.RouteMapping', []);

    const missingStartingSlash = routes.filter(({ RouteURL }) => RouteURL.match(/^(?!\/)/));
    if (missingStartingSlash.length) {
      failedPageValidations.Routes = { message: 'Routes must start with "/"!' };
    }

    const containsUppercaseLetters = routes.filter(({ RouteURL }) => RouteURL.match(/[A-Z]/));
    if (containsUppercaseLetters.length) {
      failedPageValidations.Routes = { message: 'Routes must be lowercase!' };
    }
  }

  // Component Validations
  const components = getAllComponents(pagePayload, false, true);
  const failedComponentValidations = areValidComponents(components, contractMap);

  let failedTemplateValidations = {};

  // Template Validations
  const template = get(pagePayload, 'Payload.PageTemplateName', '');
  if (template === 'Personalized Homepage Template') {
    failedTemplateValidations = isPageTemplateValid(pagePayload, personalizedHomepage);
  }

  return {
    [pagePayload.Id]: {
      ...failedPageValidations,
      ...failedTemplateValidations
    },
    ...failedComponentValidations
  };
};

/**
 * validates field and returns failedValidations object
 * @param {string} id - id of the component
 * @param {string} location - location of the field
 * @param {string} validationMessage - failed Validation message
 * @param {object} failedValidations - object containing all failed validations
 * @returns {object} object containing all failed validations
 */
export const setFailedValidationMessageForField = function setFailedValidationForField(
  id,
  location,
  validationMessage,
  failedValidations
) {
  const failedComponentValidations = failedValidations[id] || {};
  const {
    [location]: removedValidation,
    ...restFailedValidations
  } = failedComponentValidations;

  if (validationMessage) {
    restFailedValidations[location] = { message: validationMessage };
  }

  return {
    ...failedValidations,
    [id]: restFailedValidations
  };
};

/**
 * determines whether a Link URL needs to be validated and makes modifications as necessary. if the link is
 * relative and needs to be tested for mulitple countries, the link will be added to the list multiple times to be
 * tested for each country.
 * @param {object} params - params object
 * @param {string} params.linkUrl - Link URL that needs to be validated
 * @param {Array} params.countryList - list of countries link URL will be displayed in
 * @param {string} params.site - either Nordstrom or Nordstrom Rack or HauteLook
 * @returns {Array} array of links to be validated based on input link URL
 */
const filterLinkURL = function filterLinkURL({ linkUrl, countryList, site }) {
  const links = [];

  // skip over mailto and tel links
  if (/^(mailto:|tel:)/.test(linkUrl)) {
    return links;
  }

  // push absolute urls as is, without the country code or site
  else if (/^(http)/.test(linkUrl)) {
    links.push(linkUrl);
  }

  // attach country information for relative paths
  else {
    countryList.forEach(countryCode => {
      links.push({ linkUrl, countryCode, site });
    });
  }
  return links;
};

/**
 * Identifies all Link URLs in a given component that need validation
 * @param {object} params - params object
 * @param {object} params.component - component to traverse
 * @param {Array} params.countryList - list of countries link URL will be displayed in
 * @param {string} params.site - Nordstrom or Nordstrom Rack or HauteLook
 * @returns {Array} an array containing all of the Link URLs included within
 * (and nested within) the given component that will need validation
 */
export const findLinkURLsToValidate = function findLinkURLsToValidate({
  component,
  countryList,
  site
}) {
  const linksToValidate = [];
  const linkURLProperties = new Set(['LinkURL', 'sbnCompApiFetchUrl', 'targetURL', 'linkUrl']);
  const liveTextLinkURLProperties = new Set([
    'HeadlineLiveText',
    'LiveText',
    'Headline',
    'TextContents'
  ]);

  // don't attach international country codes for validation if us country code is already present
  const countries =
    countryList && countryList.includes('US')
      ? countryList.filter(countryCode => countryCode !== 'International')
      : countryList;

  /**
   * Basic helper function to recurse through a given component (obj) and identify 'LinkURL' values
   * @param {object} obj - component to traverse
   * @returns {void}
   */
  const traverse = obj => {
    const keys = Object.keys(obj);
    keys.forEach(key => {
      const linkUrl = obj[key];
      if (typeof linkUrl === 'object') {
        traverse(linkUrl);
      } else if (linkURLProperties.has(key) && linkUrl.length) {
        const links = filterLinkURL({ linkUrl, countryList: countries, site });
        linksToValidate.push(...links);
      } else if (
        liveTextLinkURLProperties.has(key) &&
        linkUrl.includes('</a>') &&
        linkUrl.length
      ) {
        const matches = linkUrl.match(/href=".*?"/g) || [];
        matches.forEach(match => {
          const links = filterLinkURL({
            linkUrl: match.slice(6, -1),
            countryList: countries,
            site
          });
          linksToValidate.push(...links);
        });
      }
    });
  };
  traverse(component);
  return linksToValidate;
};

/**
 * Identifies all Link URLs in a given component that need validation
 * @param {Array} response - array of Link URLs with status codes
 * @returns {Array} an array containing all of the Link URLs that have a status code outside of 200
 */
export const identifyInvalidLinks = function identifyInvalidLinks(response) {
  return response
    .filter(link => link.statusCode !== 200)
    .map(linkObj => {
      const { url, countryCode, site } = linkObj;
      return { url, countryCode, site };
    });
};

/**
 * Returns the failure message to display when a page or component contains invalid links
 * @param {Array} invalidLinks - array of invalid links
 * @returns {string} the failure message including details about the provided invalid links
 */
export const getInvalidLinkMessage = function getInvalidLinkMessage(invalidLinks) {
  const textList = invalidLinks
    .map((linkObj, i) =>
      linkObj.countryCode
        ? `${i + 1}. "${linkObj.url}" for country: ${linkObj.countryCode} and site: ${
            linkObj.site
          }`
        : `${i + 1}. "${linkObj.url}"`
    )
    .join('\n');
  const failureMessage = `WARNING: (${invalidLinks.length}) Unreachable ${
    invalidLinks.length === 1 ? 'URL:' : 'URLs:'
  }\n\n${textList}`;
  return failureMessage;
};
